<template>
  <PageSection :title="t('profile.editInfoForm.contactsTitle')">
    <div>
      <Input
        id="address"
        :label="t('profile.editInfoForm.address')"
        :placeholder="t('profile.editInfoForm.address')"
        :value="formValues.address"
        @input="(value: string) => updateFN('address', value)"
        :error="t('errorMessages.fieldMandatory')"
        :show-error="invalidFields.has('address')"
        :readOnly="!editable || loading"
        :input-styles="!editable ? readonlyStyles : {}"
      />
      <div class="d-flex">
        <Input
          class="col-6 py-2 pe-1"
          id="postal-code"
          :label="t('profile.editInfoForm.postalCode')"
          :placeholder="t('profile.editInfoForm.postalCode')"
          :value="formValues.postal_code"
          @input="(value: string) => updateFN('postal_code', value)"
          :error="t('errorMessages.fieldMandatory')"
          :show-error="invalidFields.has('postal_code')"
          :readOnly="!editable || loading"
          :input-styles="!editable ? readonlyStyles : {}"
        />
        <Input
          class="col-6 py-2 ps-1"
          id="city"
          :label="t('profile.editInfoForm.city')"
          :placeholder="t('profile.editInfoForm.city')"
          :value="formValues.city"
          @input="(value: string) => updateFN('city', value)"
          :error="t('errorMessages.fieldMandatory')"
          :show-error="invalidFields.has('city')"
          :readOnly="!editable || loading"
          :input-styles="!editable ? readonlyStyles : {}"
        />
      </div>
      <div class="py-2">
        <Autocomplete
          :placeholder="t('profile.editInfoForm.country')"
          :label="t('profile.editInfoForm.country')"
          :options="preparedCountries"
          :selected-option="selectedCountryOption"
          :filter-fields="['value', 'label']"
          @select-click="handleCountryChange"
          :disabled="!editable || loading"
          :input-styles="!editable ? readonlyStyles : {}"
        />
      </div>
      <div id="phone_number" class="my-2" 

      >
        <InputWrapper
          style="overflow: visible; "
          :class="invalidFields.has('phone_number') && 'error-outline'"
        >
          <div class="d-flex flex-row nowrap align-items-center">
            <div class="col-4">
              <Autocomplete
                id="phone-code"
                :placeholder="''"
                :label="t('profile.editInfoForm.phoneCode')"
                :options="phoneCodeOptions"
                :selected-option="selectedPhoneCode"
                :filter-fields="['value', 'label']"
                :containerBorderRadius="'10px 0px 0px 10px'"
                :inputClass="'px-0 px-md-3 '"
                @select-click="handlePhoneCodeChange"
                :disabled="!editable || loading"
                :readOnly="!editable || loading"
                :input-styles="!editable ? readonlyStyles : {}"
              />
            </div>
            <div class="col-8">
              <Input
                class=""
                :label="t('profile.editInfoForm.phoneNumber')"
                :placeholder="'00 00 00 00 00'"
                :value="formValues.phone_number"
                :allowed-type="'number'"
                @input="
                  (value: string) => {
                    const onlyNumbers = /^[0-9]*$/
                    if (onlyNumbers.test(value)) {
                      console.log('Phone number input changed:', value)
                      updateFN('phone_number', value)
                    } else {
                      console.warn('Invalid phone number input:', value)
                    }
                  }
                "
                :containerBorderRadius="'0px 10px 10px 0px'"
                :readOnly="!editable || loading"
                :input-styles="
                  !editable
                    ? readonlyStyles
                    : {
                        border: 'none',
                        height: '56px',
                      }
                "
                input-type="tel"
              />
            </div>
          </div>
        </InputWrapper>
        <InputFieldError
          :text="t('loginRegister.user_info_invalid_phone')"
          v-if="invalidFields.has('phone_number')"
        />
      </div>
    </div>
  </PageSection>
</template>

<script lang="ts" setup>
  // Vue
  import { computed, ComputedRef, ref, onMounted } from 'vue'

  // Components
  import PageSection from '@/components/UI/PageSection.vue'
  import Input from '@/components/UI/Input.vue'
  import Autocomplete from '@/components/UI/Autocomplete.vue'

  // Config
  import countries from 'i18n-iso-countries'
  import phone_codes, { PhoneCode } from '@/config/phone_codes'

  // Types
  import { FormProps } from './EditInfoForm.types'
  import { SelectOption } from '@/components/UI/AutoComplete.types'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Store
  import { useStore } from '@/store'
  import InputWrapper from '@/components/UI/InputWrapper.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'
  const store = useStore()

  // Props
  const props = defineProps<FormProps>()

  // Computed
  const locale: ComputedRef<string> = computed(() => {
    return store.getters.getLocale
  })

  const lang: ComputedRef<string> = computed(() => {
    return locale.value.split('-')[0] || 'en'
  })

  const selectedCountryOption: ComputedRef<SelectOption | undefined> = computed(
    () => {
      const country: SelectOption | undefined = preparedCountries.value.find(
        countryAutocompleteOption => {
          return countryAutocompleteOption.value === props.formValues.country
        }
      )

      return country
    }
  )

  const phoneCodeOptions: ComputedRef<SelectOption[]> = computed(() => {
    const options: SelectOption[] = phone_codes.map((codeObj: PhoneCode) => {
      return {
        label: `${codeObj['dial_code']} (${codeObj['code']})`,
        value: codeObj.dial_code,
      }
    })
    return options
  })

  const selectedPhoneCode: ComputedRef<SelectOption | undefined> = computed(
    () => {
      const selected = phoneCodeOptions.value.find((option: SelectOption) => {
        return option.value === props.formValues.phone_code
      })
      return selected
    }
  )

  // Refs
  const preparedCountries = ref<SelectOption[]>([])

  // Methods
  const fetchPreparedCountries = async (): Promise<void> => {
    const VIPCountryCodes = ['FR', 'BE', 'NL', 'GB']

    if (!lang.value) throw new Error('Language not defined in document.')

    const countriesForLocale = await import(
      `i18n-iso-countries/langs/${lang.value}.json`
    )
    if (!countriesForLocale)
      throw new Error(`Country JSON doesn't exist for lang: ${lang.value}`)

    countries.registerLocale(countriesForLocale)

    const countryEntries = Object.entries(countriesForLocale.countries)

    const options = countryEntries.map(([code, names]) => {
      const label = Array.isArray(names) ? names.join(' / ') : names
      return {
        value: code,
        label,
      } as SelectOption
    })

    // Move VIP countries to the top
    const sortedOptions = options.sort(
      (a, b) =>
        Number(VIPCountryCodes.includes(b.value as string)) -
        Number(VIPCountryCodes.includes(a.value as string))
    )

    // Reorder specific positions
    if (sortedOptions.length >= 3)
      [sortedOptions[0], sortedOptions[2]] = [
        sortedOptions[2],
        sortedOptions[0],
      ]
    if (sortedOptions.length >= 4)
      sortedOptions.splice(1, 0, sortedOptions.splice(3, 1)[0])

    preparedCountries.value = sortedOptions
  }

  const convertCountryNameToCode = (
    countryName: string
  ): string | undefined => {
    const formattedCountryName = countryName.includes('/')
      ? countryName.split('/')[0].trim()
      : countryName
    return countries.getAlpha2Code(formattedCountryName, lang.value)
  }

  const handlePhoneCodeChange = (option: SelectOption) => {
    if (!option.value)
      throw new Error('invalid option in handlePhoneCodeChange')
    const value = option.value
    props.updateFN('phone_code', value as string)
  }

  const handleCountryChange = (option: SelectOption) => {
    const value = convertCountryNameToCode(option.label as string)
    if (!value) throw new Error('invalid value in handleCountryChange')
    props.updateFN('country', value)
  }

  // Validate initial phone number on component mount
  const readonlyStyles = computed(() => ({
    border: 'none',
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
  }))

  onMounted(async () => {
    await fetchPreparedCountries()
  })
</script>

<style scoped>
.error-outline {
  outline: 1px solid var(--primary-orange);
  border: 1px solid var(--primary-orange)
}
</style>
